import React, { useState, useRef, useEffect } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Download, Search, Loader, Printer } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getCooperativeLogoSrc } from "@/components/ui/cooperative-logo";


type Transaction = {
  date: string;
  ref: string;
  description: string;
  amount: number;
  type: "credit" | "debit";
  balance: number;
};

const toSafeNumber = (val: any): number => {
  const n = Number(val);
  return Number.isFinite(n) ? Math.abs(n) : 0;
};

const toSafeDate = (val: any): Date | null => {
  if (!val) return null;
  const d = new Date(val);
  return Number.isFinite(d.getTime()) ? d : null;
};

const fmt = (val: number) => `${Math.round(val).toLocaleString()} RWF`;

const orgName = "CAISSE D'ENTRAIDE IPRC KARONGI";
const orgAddress = "Karongi, Rwanda";
const orgPhone = "+250 793 502 827";
const orgEmail = "caisse.iprcwest@gmail.com";

function PlaceholderStatement() {
  return (
    <>
      <div className="p-8 text-center text-muted-foreground border-b">
        <p className="text-base font-medium">Select a member to load statement</p>
        <p className="text-sm mt-1">Choose a member above to view their contribution statement.</p>
      </div>
      <div className="border-b px-6 py-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="space-y-3">
            <p className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Member Information</p>
            <div className="space-y-1 text-muted-foreground">
              <p>Member Names: —</p>
              <p>Member No: —</p>
              <p>Phone: —</p>
              <p>Currency: RWF</p>
            </div>
          </div>
          <div className="space-y-3">
            <p className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Statement Period</p>
            <div className="space-y-1 text-muted-foreground">
              <p>From: —</p>
              <p>To: —</p>
            </div>
          </div>
        </div>
      </div>
      <div className="px-6 py-4 border-b">
        <div className="bg-green-50 border border-green-800 rounded p-3">
          <p className="text-xs font-bold text-green-800 uppercase mb-2">Summary</p>
          <div className="grid grid-cols-4 gap-2 text-xs">
            {["Opening Balance", "Total Contributions", "Total Withdrawals", "Closing Balance"].map((label) => (
              <div key={label}>
                <p className="text-muted-foreground">{label}</p>
                <p className="font-bold text-gray-800">0 RWF</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="px-6 py-3">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-muted-foreground">
              <th className="text-left py-2 font-medium">Date</th>
              <th className="text-left py-2 font-medium">Ref</th>
              <th className="text-left py-2 font-medium">Description</th>
              <th className="text-right py-2 font-medium">Amount (RWF)</th>
              <th className="text-right py-2 font-medium">Balance (RWF)</th>
            </tr>
          </thead>
          <tbody>
            {[
              { date: "01/06/2026", ref: "CTR001", desc: "Monthly Contribution", amt: "10,000", bal: "10,000" },
              { date: "05/06/2026", ref: "CTR002", desc: "Additional Contribution", amt: "20,000", bal: "30,000" },
              { date: "10/06/2026", ref: "LOAN-1", desc: "Loan Disbursement", amt: "(50,000)", bal: "(20,000)" },
            ].map((r, i) => (
              <tr key={i} className="border-b border-gray-100">
                <td className="py-2 text-muted-foreground">{r.date}</td>
                <td className="py-2 text-muted-foreground">{r.ref}</td>
                <td className="py-2">{r.desc}</td>
                <td className="py-2 text-right">{r.amt}</td>
                <td className="py-2 text-right font-medium">{r.bal}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function LiveStatement({
  member,
  transactions,
  totalCredits,
  totalDebits,
  closingBalance,
  transactionsCount,
}: {
  member: { label: string; teacherId: string; phone: string };
  transactions: Transaction[];
  totalCredits: number;
  totalDebits: number;
  closingBalance: number;
  transactionsCount: number;
}) {
  const latest = transactions.slice(-10);

  if (transactions.length === 0) {
    return (
      <div className="px-6 py-16 text-center text-muted-foreground italic border-b">
        No contribution records found for this period.
      </div>
    );
  }

  return (
    <>
      <div className="px-6 py-3">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-muted-foreground">
              <th className="text-left py-2 font-medium">Date</th>
              <th className="text-left py-2 font-medium">Ref</th>
              <th className="text-left py-2 font-medium">Description</th>
              <th className="text-right py-2 font-medium">Amount (RWF)</th>
              <th className="text-right py-2 font-medium">Balance (RWF)</th>
            </tr>
          </thead>
          <tbody>
            {latest.map((t, i) => {
              const d = toSafeDate(t.date);
              return (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2 text-muted-foreground">{d ? format(d, "yyyy-MM-dd") : "N/A"}</td>
                  <td className="py-2 text-muted-foreground">{t.ref}</td>
                  <td className="py-2">{t.description}</td>
                  <td className={`py-2 text-right ${t.type === "debit" ? "text-red-600" : ""}`}>
                    {t.type === "credit"
                      ? Math.round(t.amount).toLocaleString()
                      : `(${Math.round(t.amount).toLocaleString()})`}
                  </td>
                  <td className="py-2 text-right font-medium">{Math.round(t.balance).toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {transactionsCount > 10 && (
          <p className="text-xs text-muted-foreground text-center py-3 border-t border-gray-100">
            Showing latest 10 of {transactionsCount} transactions. Download PDF for full statement.
          </p>
        )}
      </div>
    </>
  );
}

export default function MemberContributionStatement() {
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [memberSearchOpen, setMemberSearchOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [isGenerating, setIsGenerating] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "print-statement-styles";
    style.textContent = `
      @media print {
        body * { visibility: hidden; }
        .statement-preview-wrapper, .statement-preview-wrapper * { visibility: visible; }
        .statement-preview-wrapper { position: absolute; left: 0; top: 0; width: 100%; }
        @page { margin: 12mm; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById("print-statement-styles");
      if (el) el.remove();
    };
  }, []);

  const { data: teachers = [], isLoading: isTeachersLoading } = useQuery(
    ["teachers-for-statement"],
    async () => {
      const { data } = await api.get("/teachers?limit=1000");
      const results = data.results || data.data || [];
      return results.map((t: any) => ({
        label: t.fullName || `${t.firstName || ""} ${t.lastName || ""}`.trim(),
        value: t.id,
        teacherId: t.teacherId,
        phone: t.phone,
      }));
    }
  );

  const selectedMember = teachers.find((t) => t.value === selectedMemberId);

  const { data: statementData, isLoading: isStatementLoading } = useQuery(
    [
      "contribution-statement-data",
      selectedMemberId,
      startDate?.toISOString() ?? "all",
      endDate?.toISOString() ?? "all",
    ],
    async () => {
      if (!selectedMemberId) return null;

      const fromDate = startDate ?? new Date(0);
      const toDate = endDate ?? new Date();

      const [contributionsRes, loansRes] = await Promise.all([
        api.get(`/contributions/teacher/${selectedMemberId}`).catch(() => ({ data: { data: [] } })),
        api.get(`/loans/teacher/${selectedMemberId}`).catch(() => ({ data: { data: [] } })),
      ]);

      const contributions: Transaction[] = (
        contributionsRes.data?.data || contributionsRes.data?.results || []
      ).map((c: any) => ({
        date: c.contributionDate || c.createdAt || c.date,
        ref: c.referenceNumber || `CONT-${c.id}`,
        description:
          c.contributionType === "Savings"
            ? "Monthly Contribution"
            : c.contributionType === "Loan Repayment"
            ? "Loan Repayment"
            : c.contributionType === "Shares"
            ? "Share Purchase"
            : c.contributionType === "Fine"
            ? "Fine Payment"
            : c.contributionType === "Registration Fee"
            ? "Registration Fee"
            : c.contributionType || "Contribution",
        amount: toSafeNumber(c.amount || c.depositAmount),
        type: "credit" as const,
      }));

      const loans: Transaction[] = (
        loansRes.data?.data || loansRes.data?.results || loansRes.data || []
      ).map((l: any) => ({
        date: l.disbursementDate || l.applicationDate || l.date,
        ref: `LOAN-${l.id}`,
        description: "Loan Disbursement",
        amount: toSafeNumber(l.disbursedAmount || l.amount),
        type: "debit" as const,
      }));

      const allTransactions = [...contributions, ...loans]
        .filter((t) => {
          const d = toSafeDate(t.date);
          if (!d) return false;
          return d >= fromDate && d <= toDate;
        })
        .sort((a, b) => {
          const da = toSafeDate(a.date);
          const db = toSafeDate(b.date);
          if (!da && !db) return 0;
          if (!da) return 1;
          if (!db) return -1;
          return da.getTime() - db.getTime();
        });

      let running = 0;
      const withBalance = allTransactions.map((t) => {
        running += t.type === "credit" ? t.amount : -t.amount;
        return { ...t, balance: running };
      });

      const totalCredits = withBalance.filter((t) => t.type === "credit").reduce((s, t) => s + t.amount, 0);
      const totalDebits = withBalance.filter((t) => t.type === "debit").reduce((s, t) => s + t.amount, 0);
      const closingBalance = running;

      return { transactions: withBalance, fromDate, toDate, totalCredits, totalDebits, closingBalance };
    },
    { enabled: !!selectedMemberId, keepPreviousData: true }
  );

  const generatePDF = async () => {
    if (!selectedMemberId || !selectedMember || !statementData) {
      toast.error("Please select a member");
      return;
    }
    setIsGenerating(true);
    try {
      const { transactions, fromDate, toDate, totalCredits, totalDebits } = statementData;
      const member = selectedMember;

      const withBalance = transactions;
      const closingBalance = transactions.length > 0 ? transactions[transactions.length - 1].balance : 0;

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pw = 210;
      const m = 15;
      const cw = pw - 2 * m;

      const drawHeader = () => {
        try {
          pdf.addImage(getCooperativeLogoSrc(), "JPEG", m, 10, 25, 25);
        } catch {
          pdf.setFontSize(10);
          pdf.setTextColor(40, 80, 40);
          pdf.text("IKIMINA", m + 2, 22);
        }
        pdf.setFontSize(16);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0, 0, 0);
        pdf.text(orgName, pw / 2, 18, { align: "center" });
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(100, 100, 100);
        pdf.text(`${orgAddress} | ${orgPhone} | ${orgEmail}`, pw / 2, 24, { align: "center" });
        pdf.setDrawColor(0, 100, 0);
        pdf.setLineWidth(0.5);
        pdf.line(m, 28, pw - m, 28);
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.text(`Printed: ${format(new Date(), "yyyy-MM-dd HH:mm")}`, m, 33);
        pdf.setFontSize(18);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0, 60, 0);
        pdf.text("Member Contribution Statement", pw / 2, 43, { align: "center" });
        pdf.setDrawColor(0, 100, 0);
        pdf.setLineWidth(0.3);
        pdf.line(m, 46, pw - m, 46);
      };

      const drawFooter = (pageNum: number, totalPages: number) => {
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.3);
        pdf.line(m, 285, pw - m, 285);
        pdf.setFontSize(7);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(120, 120, 120);
        pdf.text(`Printed on: ${format(new Date(), "yyyy-MM-dd HH:mm")}`, m, 290);
        const pt = totalPages > 1 ? `Page ${pageNum} of ${totalPages}` : `Page ${pageNum}`;
        pdf.text(pt, pw - m, 290, { align: "right" });
      };

      drawHeader();

      const hh = 50;
      const miy = hh + 2;
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(50, 50, 50);

      pdf.setFont("helvetica", "bold");
      pdf.text("MEMBER INFORMATION", m, miy);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      let iy = miy + 5;
      for (const line of [
        `Member Names: ${member.label}`,
        `Member No: ${member.teacherId || "N/A"}`,
        `Phone: ${member.phone || "N/A"}`,
        `Currency: RWF`,
      ]) {
        pdf.text(line, m, iy);
        iy += 4.2;
      }

      const irx = pw / 2 + 5;
      pdf.setFont("helvetica", "bold");
      pdf.text("STATEMENT PERIOD", irx, miy);
      pdf.setFont("helvetica", "normal");
      let py = miy + 5;
      const fromLabel = startDate ? format(startDate, "yyyy-MM-dd") : "Beginning of records";
      const toLabel = endDate ? format(endDate, "yyyy-MM-dd") : format(toDate, "yyyy-MM-dd");
      for (const line of [`From: ${fromLabel}`, `To: ${toLabel}`]) {
        pdf.text(line, irx, py);
        py += 4.2;
      }

      const sy = miy + 28;
      pdf.setDrawColor(0, 80, 0);
      pdf.setFillColor(245, 250, 240);
      pdf.roundedRect(m, sy, cw, 28, 2, 2, "FD");
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(0, 60, 0);
      pdf.text("SUMMARY", m + 3, sy + 5);

      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(50, 50, 50);

      const summaryItems = [
        { label: "Opening Balance", value: fmt(0) },
        { label: "Total Contributions", value: fmt(totalCredits) },
        { label: "Total Withdrawals", value: fmt(totalDebits) },
        { label: "Closing Balance", value: fmt(closingBalance) },
      ];
      const scw = cw / 4;
      summaryItems.forEach((item, idx) => {
        const x = m + 3 + idx * scw;
        pdf.setFont("helvetica", "normal");
        pdf.text(item.label, x, sy + 12);
        pdf.setFont("helvetica", "bold");
        pdf.text(item.value, x, sy + 19);
      });

      const tsy = sy + 36;

      if (withBalance.length === 0) {
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "italic");
        pdf.setTextColor(100, 100, 100);
        pdf.text("No contribution records found for this period.", m, tsy);
      } else {
        const tableData = withBalance.map((t) => {
          const d = toSafeDate(t.date);
          return [
            d ? format(d, "yyyy-MM-dd") : "N/A",
            t.ref || "N/A",
            t.description,
            t.type === "credit"
              ? `${Math.round(t.amount).toLocaleString()}`
              : `(${Math.round(t.amount).toLocaleString()})`,
            `${Math.round(t.balance).toLocaleString()}`,
          ];
        });

        autoTable(pdf, {
          startY: tsy,
          head: [
            [
              { content: "Date", styles: { halign: "center" } },
              { content: "Ref", styles: { halign: "center" } },
              { content: "Description" },
              { content: "Amount (RWF)", styles: { halign: "right" } },
              { content: "Balance (RWF)", styles: { halign: "right" } },
            ],
          ],
          body: tableData,
          foot: [
            [
              "",
              "",
              { content: "Closing Balance", styles: { fontStyle: "bold" } },
              "",
              { content: fmt(closingBalance), styles: { halign: "right", fontStyle: "bold" } },
            ],
          ],
          theme: "grid",
          headStyles: { fillColor: [0, 80, 0], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7, halign: "center" },
          bodyStyles: { fontSize: 7, cellPadding: 1.5 },
          alternateRowStyles: { fillColor: [245, 248, 242] },
          footStyles: { fillColor: [240, 245, 238], fontSize: 7, fontStyle: "bold" },
          columnStyles: {
            0: { cellWidth: 25, halign: "center" },
            1: { cellWidth: 28, halign: "center" },
            2: { cellWidth: cw - 113 },
            3: { cellWidth: 30, halign: "right" },
            4: { cellWidth: 30, halign: "right" },
          },
          margin: { top: 10, right: m, bottom: 25, left: m },
          didDrawPage: (data: any) => {
            const totalPages = (pdf as any).internal?.getNumberOfPages?.() ?? 1;
            drawFooter(data.pageNumber, totalPages);
          },
        });
      }

      const totalPdfPages = (pdf as any).internal?.getNumberOfPages?.() ?? 1;
      for (let i = 1; i <= totalPdfPages; i++) {
        pdf.setPage(i);
        drawFooter(i, totalPdfPages);
      }

      const safeName = (member.label || "member").replace(/\s+/g, "-").toLowerCase();
      const fileName = `contribution-statement-${safeName}-${format(new Date(), "yyyyMMdd")}.pdf`;
      pdf.save(fileName);
      toast.success("Contribution statement downloaded successfully");
    } catch (error) {
      console.error("PDF statement generation failed:", error);
      toast.error("Failed to generate contribution statement");
    } finally {
      setIsGenerating(false);
    }
  };

  const isLive = !!selectedMember && !!statementData;
  const transactions = statementData?.transactions ?? [];
  const totalCredits = statementData?.totalCredits ?? 0;
  const totalDebits = statementData?.totalDebits ?? 0;
  const closingBalance = statementData?.closingBalance ?? 0;

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold tracking-tight">Member Contribution Statement</h1>

      {/* Filter + Actions bar */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
        <div className="sm:col-span-2 space-y-1.5">
          <Label className="text-xs">Member</Label>
          <Popover open={memberSearchOpen} onOpenChange={setMemberSearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={memberSearchOpen}
                className="w-full justify-between font-normal h-9 text-sm"
              >
                {selectedMember ? selectedMember.label : "Select member..."}
                <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput placeholder="Search member..." />
                <CommandList>
                  {isTeachersLoading ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">Loading members...</div>
                  ) : teachers.length === 0 ? (
                    <CommandEmpty>No members found</CommandEmpty>
                  ) : (
                    <CommandGroup>
                      {teachers.map((teacher) => (
                        <CommandItem
                          key={teacher.value}
                          value={teacher.label}
                          onSelect={() => {
                            setSelectedMemberId(teacher.value);
                            setMemberSearchOpen(false);
                          }}
                        >
                          {teacher.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">From Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal h-9 text-sm",
                  !startDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, "PPP") : "Start date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">To Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal h-9 text-sm",
                  !endDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, "PPP") : "End date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={setEndDate}
                disabled={(date) => (startDate ? date < startDate : false)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            className="h-9 flex-1"
            disabled={!selectedMemberId}
            onClick={() => window.print()}
          >
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
          <Button
            onClick={generatePDF}
            disabled={!selectedMemberId || isGenerating}
            className="h-9 flex-1 bg-[#4dbd07] hover:bg-[#3d9a06] text-white"
          >
            {isGenerating ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                PDF
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Statement preview card */}
      <div ref={previewRef} className="statement-preview-wrapper border rounded-lg bg-white shadow-sm overflow-hidden">
          {/* Header */}
          <div className="flex items-center px-6 pt-6 pb-3">
            <div className="w-16 h-16 flex items-center justify-center mr-4 bg-gray-100 rounded overflow-hidden">
              {logoFailed ? (
                <span className="text-xs font-bold text-green-700">CEIK</span>
              ) : (
                <img
                  src={getCooperativeLogoSrc()}
                  alt="CAISSE D'ENTRAIDE IPRC KARONGI"
                  className="w-full h-full object-contain"
                  onError={() => setLogoFailed(true)}
                />
              )}
            </div>
            <div className="flex-1">
              <p className="text-lg font-bold">{orgName}</p>
              <p className="text-xs text-gray-500">{orgAddress} | {orgPhone} | {orgEmail}</p>
            </div>
          </div>
          <hr className="border-green-700 border-t-2 mx-6" />
          <p className="text-center text-green-800 text-lg font-bold py-2">Member Contribution Statement</p>
          <hr className="border-green-700 border-t mx-6" />

          {/* Content area */}
          {!selectedMemberId && <PlaceholderStatement />}

          {isStatementLoading && selectedMemberId && (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader className="h-5 w-5 animate-spin mr-2" />
              Loading statement...
            </div>
          )}

          {isLive && !isStatementLoading && (
            <>
              {/* Member info + Period */}
              <div className="border-b px-6 py-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-3">
                    <p className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Member Information</p>
                    <div className="space-y-1">
                      <p><span className="text-muted-foreground">Member Names:</span> {selectedMember.label}</p>
                      <p><span className="text-muted-foreground">Member No:</span> {selectedMember.teacherId || "N/A"}</p>
                      <p><span className="text-muted-foreground">Phone:</span> {selectedMember.phone || "N/A"}</p>
                      <p><span className="text-muted-foreground">Currency:</span> RWF</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <p className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Statement Period</p>
                    <div className="space-y-1">
                      <p><span className="text-muted-foreground">From:</span> {startDate ? format(startDate, "yyyy-MM-dd") : "Beginning of records"}</p>
                      <p><span className="text-muted-foreground">To:</span> {endDate ? format(endDate, "yyyy-MM-dd") : "Today"}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="px-6 py-4 border-b">
                <div className="bg-green-50 border border-green-800 rounded p-3">
                  <p className="text-xs font-bold text-green-800 uppercase mb-2">Summary</p>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div>
                      <p className="text-muted-foreground">Opening Balance</p>
                      <p className="font-bold text-gray-800">{fmt(0)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Total Contributions</p>
                      <p className="font-bold text-gray-800">{fmt(totalCredits)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Total Withdrawals</p>
                      <p className="font-bold text-gray-800">{fmt(totalDebits)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Closing Balance</p>
                      <p className="font-bold text-gray-800">{fmt(closingBalance)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transactions */}
              <LiveStatement
                member={selectedMember}
                transactions={transactions}
                totalCredits={totalCredits}
                totalDebits={totalDebits}
                closingBalance={closingBalance}
                transactionsCount={transactions.length}
              />
            </>
          )}

          {/* Footer */}
          <div className="px-6 py-3 border-t text-xs text-gray-400 flex justify-between">
            <span>Printed on: {format(new Date(), "yyyy-MM-dd HH:mm")}</span>
            <span>Page 1 of 1</span>
          </div>
        </div>
      </div>
  );
}
